import React, { useState, useCallback } from "react";
import { Select } from "antd";
import type { SelectProps } from "antd";
import styles from "./style.module.scss";
import SearchIcon from "assets/icons/SearchIcon";
import EmptyState from "./EmptyState";
import { SelectOption } from "./SelectOption";

export type Option = {
  label: string;
  value: string;
  title?: string;
};

export interface SelectListProps {
  options: Option[];
  value?: string[];
  defaultValue?: string[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  onChange?: (value: string[]) => void;
  loading?: boolean;
  mode?: "multiple";
  open?: boolean;
  defaultOpen?: boolean;
  placement?: "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
  onDropdownVisibleChange?: (open: boolean) => void;
  showSearchIcon?: boolean;
  optionRender?: SelectProps["optionRender"];
  dropdownClassName?: string;
  getPopupContainer?: SelectProps["getPopupContainer"];
  autoFocus?: boolean;
}

export default function SelectList({
  options,
  defaultValue,
  placeholder = "Please select",
  disabled = false,
  allowClear = true,
  onChange,
  value,
  loading = false,
  mode,
  open,
  defaultOpen,
  placement,
  onDropdownVisibleChange,
  showSearchIcon = true,
  optionRender: customOptionRender,
  dropdownClassName,
  getPopupContainer,
  autoFocus = true,
}: SelectListProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(
    value || defaultValue || [],
  );

  const getOptionRender = useCallback((): SelectProps["optionRender"] => {
    if (customOptionRender) {
      return customOptionRender;
    }

    if (!mode) {
      return undefined;
    }

    return (oriOption) => {
      const option = oriOption as Option;
      return (
        <SelectOption
          selectedValues={selectedValues}
          value={option.value}
          label={option.label}
        />
      );
    };
  }, [customOptionRender, mode, selectedValues]);

  return (
    <div className={styles.selectWrapper}>
      <Select<string[], Option>
        mode={mode}
        open={open}
        defaultOpen={defaultOpen}
        autoFocus={autoFocus}
        prefix={showSearchIcon ? <SearchIcon /> : null}
        suffixIcon={false}
        loading={loading}
        allowClear={allowClear}
        placement={placement}
        showSearch={{
          filterOption: (input, option) =>
            (option?.label || "")
              .toLowerCase()
              .includes(input.toLowerCase().trim()),
        }}
        placeholder={placeholder}
        disabled={disabled}
        defaultValue={defaultValue}
        value={value}
        options={options.map((o) => ({ ...o, title: "" }))}
        menuItemSelectedIcon={null}
        className={styles.select}
        notFoundContent={<EmptyState />}
        onOpenChange={onDropdownVisibleChange}
        onChange={(val) => {
          setSelectedValues(val);
          onChange?.(val);
        }}
        onClear={() => {
          setSelectedValues([]);
          onChange?.([]);
        }}
        optionRender={getOptionRender()}
        classNames={{
          popup: { root: dropdownClassName },
        }}
        getPopupContainer={getPopupContainer}
      />
    </div>
  );
}
